import type { Board, Point } from 'jsxgraph';
import { createHandleSync, JsxParamStore, type HandleParamMapping } from '@examdojo/core/widget';
import type { HandleBehavior } from '../../../graphpad.model';
import type { HandleConfigs } from '../../functions/function.model';

/**
 * Creates handles for a graphpad function based on handle configurations.
 * Each behavior can have its own param mapping.
 * Autowires sync callbacks to keep handles in sync with params.
 *
 * @returns Map of handle behaviors to points, and array of all handle points for cleanup
 */
export function createHandlesFromConfigs(
  board: Board,
  paramStore: JsxParamStore,
  handleConfigs: HandleConfigs,
  initialParams: Record<string, number>,
  strokeColor: string,
): { handles: Map<HandleBehavior, Point>; handlePoints: Point[] } {
  const handles = new Map<HandleBehavior, Point>();
  const handlePoints: Point[] = [];

  for (const [behavior, config] of Object.entries(handleConfigs) as Array<[HandleBehavior, HandleParamMapping]>) {
    if (!config) {
      continue;
    }

    // Compute initial position using transform if available
    const [initX, initY] = config.toPosition
      ? config.toPosition(initialParams)
      : [initialParams[config.xParam], initialParams[config.yParam]];

    // strokeColor param here is actually the fill color (main visual color)
    const handle = board.create('point', [initX, initY], {
      visible: false,
      fixed: true,
      name: '',
      size: 6,
      fillColor: strokeColor,
      strokeColor: 'transparent',
      strokeWidth: 0,
      withLabel: false,
    }) as Point;

    handles.set(behavior, handle);
    handlePoints.push(handle);

    // Each handle syncs using its own config
    paramStore.onSync(createHandleSync(paramStore, handle, config));
  }

  return { handles, handlePoints };
}

/**
 * Gets handle position based on mapping.
 * Uses transform if available, otherwise falls back to direct param values.
 */
export function getHandlePosition(params: Record<string, number>, mapping: HandleParamMapping): [number, number] {
  if (mapping.toPosition) {
    return mapping.toPosition(params);
  }
  return [params[mapping.xParam], params[mapping.yParam]];
}

/**
 * Computes new params from handle drag based on behavior.
 * Uses fromPosition transform if available, otherwise falls back to direct mapping.
 */
export function paramsFromHandle<TParams extends Record<string, number>>(
  behavior: HandleBehavior,
  handleX: number,
  handleY: number,
  currentParams: TParams,
  mapping: HandleParamMapping,
): TParams {
  // Use custom transform if available
  if (mapping.fromPosition) {
    const newParams = mapping.fromPosition(handleX, handleY, currentParams);
    switch (behavior) {
      case 'translate':
        return { ...currentParams, ...newParams };
      case 'translate-x':
        // Only apply xParam from the transform
        return { ...currentParams, [mapping.xParam]: newParams[mapping.xParam] ?? currentParams[mapping.xParam] };
      case 'translate-y':
        // Only apply yParam from the transform
        return { ...currentParams, [mapping.yParam]: newParams[mapping.yParam] ?? currentParams[mapping.yParam] };
      default:
        return currentParams;
    }
  }

  // Default behavior: direct mapping
  switch (behavior) {
    case 'translate':
      return { ...currentParams, [mapping.xParam]: handleX, [mapping.yParam]: handleY };
    case 'translate-x':
      return { ...currentParams, [mapping.xParam]: handleX };
    case 'translate-y':
      return { ...currentParams, [mapping.yParam]: handleY };
    default:
      return currentParams;
  }
}
